import { objectParticle, topicParticle } from '../api/utils/util-func';

/**
 * 폼 입력 검증 규칙 모음.
 *
 * 모든 항목은 아래 세 가지를 같은 형태로 내보낸다.
 * - `<FIELD>_MESSAGE` : 형식 오류 메시지. placeholder 등 화면 문구로도 쓴다.
 * - `is<Field>Valid`  : 값 하나를 직접 검사하는 함수. alert 같은 명령형 검사에 쓴다.
 * - `<field>Rules`    : react-hook-form 의 `rules` 에 그대로 넘기는 규칙 팩토리.
 *
 * 규칙 팩토리는 모두 {@link FieldRuleOptions} 하나만 받고, 검사는 항상 `validate` 로
 * 수행한다. `pattern` / `minLength` 같은 규칙을 섞어 쓰지 않으므로 전화번호(하이픈 제거)나
 * 생일(미완성 값)처럼 정규식만으로 안 되는 항목도 같은 방식으로 다룰 수 있다.
 */

/** 규칙 팩토리 공통 옵션. 모든 `<field>Rules` 가 이 형태만 받는다. */
export type FieldRuleOptions<TValue = any> = {
  /**
   * 필수 입력 여부.
   * `true` 면 `label` 로 만든 기본 메시지를, 문자열이면 그 문자열을 메시지로 쓴다.
   * 생략하면 값이 비어 있어도 통과시킨다.
   */
  required?: boolean | string;
  /** 메시지에 넣을 항목명. 생략하면 항목별 기본 이름을 쓴다. (예: '보호자 이름') */
  label?: string;
  /** 화면마다 다른 추가 검사. 형식 검사(`format`) 와 함께 실행된다. */
  validate?: Record<string, (value: TValue) => boolean | string>;
  /**
   * 이 항목이 바뀔 때 함께 다시 검사할 다른 항목의 이름.
   * 국가 코드처럼 다른 항목의 검사 기준이 되는 값에 쓴다.
   * (국가 코드를 고치면 이미 떠 있는 전화번호 오류도 새 기준으로 다시 판정된다)
   */
  deps?: string | string[];
};

/**
 * 항목 하나의 검증 방법 정의. {@link createRules} 에만 쓴다.
 */
type FieldRuleSpec<TValue> = {
  /** `label` 을 넘기지 않았을 때 메시지에 쓰는 항목명. */
  defaultLabel: string;
  /** 형식 검사. 값이 비어 있는 경우는 `required` 가 맡으므로 여기까지 오지 않는다. */
  isValid: (value: TValue) => boolean;
  /** 형식 오류 메시지. 값에 따라 메시지가 갈리는 항목은 `value` 를 함께 본다. */
  message: (label: string, value: TValue) => string;
};

/** 공백만 있는 값도 비어 있는 것으로 본다. */
const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && !value);

/**
 * 항목 정의로부터 규칙 팩토리를 만든다.
 * 만들어진 팩토리는 `{ required?, validate: { format, ...추가검사 } }` 를 돌려준다.
 */
const createRules =
  <TValue>({ defaultLabel, isValid, message }: FieldRuleSpec<TValue>) =>
  ({
    required,
    label = defaultLabel,
    validate,
    deps,
  }: FieldRuleOptions<TValue> = {}) => ({
    ...(required
      ? {
          required:
            typeof required === 'string'
              ? required
              : `${objectParticle(label)} 입력해주세요`,
        }
      : {}),
    ...(deps ? { deps } : {}),
    validate: {
      format: (value: TValue) =>
        isEmpty(value) || isValid(value) || message(label, value),
      ...validate,
    },
  });

//===============================================================================================================
// 비밀번호

/** 영문 / 숫자 / 특수문자(!@#$%^&*)를 각각 1자 이상 포함한 8~20자. */
export const PASSWORD_PATTERN =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,20}$/;

export const PASSWORD_MESSAGE =
  '영문, 숫자, 특수문자를 포함하여 8~20자로 입력해주세요. 특수문자는 !@#$%^&* 만 사용 가능합니다.';

export const isValidPassword = (value?: string) =>
  PASSWORD_PATTERN.test(value ?? '');

export const passwordRules = createRules<string | undefined>({
  defaultLabel: '비밀번호',
  isValid: isValidPassword,
  message: () => PASSWORD_MESSAGE,
});

//===============================================================================================================
// 이메일

export const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const EMAIL_MESSAGE = '이메일 형식이 아닙니다.';

export const isValidEmail = (value?: string) => EMAIL_PATTERN.test(value ?? '');

export const emailRules = createRules<string | undefined>({
  defaultLabel: '이메일',
  isValid: isValidEmail,
  message: () => EMAIL_MESSAGE,
});

//===============================================================================================================
// 아이디

/**
 * 아이디: 영문자로 시작하는 영문 / 숫자 / _ 조합 5~20자, 또는 이메일 형식.
 * 이메일 쪽은 {@link EMAIL_PATTERN} 을 이어붙여 두 규칙이 어긋나지 않게 한다.
 */
export const UID_PATTERN = new RegExp(
  `^[a-zA-Z][a-zA-Z0-9_]{4,19}$|${EMAIL_PATTERN.source}`,
);

/**
 * 일반 아이디의 길이 제한. {@link UID_PATTERN} 이 이미 강제하는 값이라,
 * 여기서는 '형식이 틀렸다' 대신 길이 오류 메시지를 따로 내주는 데만 쓴다.
 * 이메일 형식 아이디에는 적용하지 않는다.
 */
export const UID_MIN_LENGTH = 5;
export const UID_MAX_LENGTH = 20;

export const UID_MESSAGE =
  '아이디는 영문자로 시작해야 하며, 영문자와 숫자, _로 구성될 수 있습니다. 이외 특수문자는 사용할 수 없습니다. 또는 이메일 형식이어야 합니다.';

/** 아이디 형식 검사. 길이는 보지 않는다. 길이까지 함께 보려면 {@link uidRules} 를 쓴다. */
export const isValidUid = (value?: string) => UID_PATTERN.test(value ?? '');

/**
 * 검사는 {@link UID_PATTERN} 하나로 하고, 길이 오류일 때만 메시지를 갈라준다.
 * 중복확인처럼 화면마다 다른 검사는 `validate` 옵션으로 덧붙인다.
 */
export const uidRules = createRules<string | undefined>({
  defaultLabel: '아이디',
  isValid: isValidUid,
  message: (label, value) => {
    const uid = value ?? '';
    // 이메일 형식 아이디는 길이 제한이 없으므로 형식 메시지만 낸다.
    if (uid.includes('@')) return UID_MESSAGE;
    if (uid.length < UID_MIN_LENGTH)
      return `${topicParticle(label)} ${UID_MIN_LENGTH}자 이상으로 입력해주세요.`;
    if (uid.length > UID_MAX_LENGTH)
      return `${topicParticle(label)} ${UID_MAX_LENGTH}자 이하로 입력해주세요.`;
    return UID_MESSAGE;
  },
});

//===============================================================================================================
// 국가 코드

/** 국가 코드 입력 자릿수. */
export const COUNTRY_CODE_MAX_LENGTH = 3;

/** 국가 코드 기본값. 값에는 '+' 없이 숫자만 둔다. */
export const DEFAULT_COUNTRY_CODE = '82';

/**
 * 국가 코드: 숫자 1~3자리. (예: 82)
 * '+' 는 입력창에 고정으로 붙여 보여주고 값에는 넣지 않는다.
 */
export const COUNTRY_CODE_PATTERN = /^[0-9]{1,3}$/;

export const COUNTRY_CODE_MESSAGE = `숫자 ${COUNTRY_CODE_MAX_LENGTH}자리까지 입력하세요 (예: 82)`;

/** 국가 코드 입력 아래에 띄워두는 안내. */
export const COUNTRY_CODE_HELP = '한국: 82, 그 외 국가 직접 입력';

/** 국가 코드 미입력 안내. 무엇을 넣어야 하는지 함께 알려준다. */
export const COUNTRY_CODE_REQUIRED_MESSAGE = `국가 코드를 입력해주세요 (${COUNTRY_CODE_HELP})`;

/** 국가 코드 입력값에서 숫자만 남긴다. ('+' 를 붙여 넣어도 숫자만 남는다) */
export const normalizeCountryCode = (value: string) =>
  value.replace(/[^0-9]/g, '').slice(0, COUNTRY_CODE_MAX_LENGTH);

/**
 * 폼에서 쓸 국가 코드.
 * '+82' 형태로 저장된 값도 있으므로 숫자만 남기고, 비어 있으면 기본값을 쓴다.
 */
export const toCountryCodeValue = (countryCode?: string | null) =>
  normalizeCountryCode(countryCode ?? '') || DEFAULT_COUNTRY_CODE;

/** 저장 요청에 쓸 국가 코드. 저장 API는 '+'와 1~3자리 숫자 형태만 받는다. */
export const toCountryCodeRequest = (countryCode?: string | null) =>
  `+${toCountryCodeValue(countryCode)}`;

export const isValidCountryCode = (value?: string) =>
  COUNTRY_CODE_PATTERN.test(value ?? '');

export const countryCodeRules = createRules<string | undefined>({
  defaultLabel: '국가 코드',
  isValid: isValidCountryCode,
  message: () => COUNTRY_CODE_MESSAGE,
});

/**
 * 국내(한국) 번호인지. 전화번호의 검사 기준과 화면 표기를 가르는 값이다.
 * 국가 코드를 입력받지 않는 화면(대부분)은 값을 넘기지 않으므로 국내로 본다.
 */
export const isKoreaCountryCode = (countryCode?: string | null) =>
  toCountryCodeValue(countryCode) === DEFAULT_COUNTRY_CODE;

//===============================================================================================================
// 전화번호

/**
 * 전화번호는 함께 입력받은 국가 코드로 검사 기준을 나눈다.
 * - 국내(+82): {@link PHONE_PATTERN}. 0으로 시작하는 기존 규칙 그대로다.
 * - 해외: {@link INTERNATIONAL_PHONE_PATTERN}. 나라별 번호 체계까지는 보지 않는다.
 *
 * 국가 코드를 입력받는 화면은 학생 최초 로그인 / 내 정보의 보호자 정보 두 곳뿐이고,
 * 나머지 화면은 국가 코드를 '82' 로 고정해 두었으므로 기존 동작 그대로다.
 *
 * @todo 해외로 문자 발송이나 본인인증이 붙어 나라별 정확한 검증이 필요해지면
 * `libphonenumber-js` 를 들여와
 * `parsePhoneNumberFromString(`+${countryCode}${phone}`)?.isValid()` 한 번으로 바꾼다.
 * 국가 선택 UI(드롭다운)와 나라별 표기 포맷도 그때 함께 정리한다.
 * 지금은 번들이 80~145KB 늘어나는 데 비해 얻는 게 적어 미뤄둔다.
 */

/**
 * 국내 전화번호: 하이픈을 제외한 숫자 기준. 휴대폰과 지역번호(02 포함)를 모두 허용한다.
 */
export const PHONE_PATTERN = /^0(?:2|[0-9]{2})(?:[0-9]{3,4})[0-9]{4}$/;

export const PHONE_MESSAGE = '올바른 전화번호를 입력하세요 (예: 01012345678)';

/**
 * 해외 전화번호: 국가 코드를 제외한 번호 4~14자리.
 * E.164 기준으로 국가 코드까지 합쳐 15자리를 넘지 않고, 국제 표기에서는 국내 통화용
 * 앞자리 0(트렁크 프리픽스)을 빼므로 0으로 시작하지 않는다.
 * 나라마다 다른 자릿수는 보지 않고, 형식이 명백히 아닌 값만 걸러낸다.
 */
export const INTERNATIONAL_PHONE_PATTERN = /^[1-9][0-9]{3,13}$/;

export const INTERNATIONAL_PHONE_MESSAGE =
  '국가 코드를 제외한 번호를 앞자리 0 없이 입력하세요 (예: 2015550123)';

/**
 * 전화번호 입력값에서 숫자만 남긴다.
 * 하이픈은 `phoneFormat` 으로 화면에만 붙이고, 폼 값과 서버 전송값은 항상 숫자만 유지한다.
 */
export const normalizePhone = (value: string) => value.replace(/[^0-9]/g, '');

/** 하이픈을 제거한 뒤 국내 전화번호 형식을 검사한다. */
export const isValidPhone = (value?: string) =>
  PHONE_PATTERN.test(normalizePhone(value ?? ''));

/** 하이픈을 제거한 뒤 해외 전화번호 형식을 검사한다. */
export const isValidInternationalPhone = (value?: string) =>
  INTERNATIONAL_PHONE_PATTERN.test(normalizePhone(value ?? ''));

/** 국가 코드에 맞는 전화번호 형식인지 검사한다. */
export const isValidPhoneOf = (countryCode?: string | null, value?: string) =>
  isKoreaCountryCode(countryCode)
    ? isValidPhone(value)
    : isValidInternationalPhone(value);

export type PhoneRuleOptions = FieldRuleOptions<string | undefined> & {
  /**
   * 함께 입력받는 국가 코드. 숫자만 있는 값('82')과 '+82' 형태를 모두 받는다.
   * 생략하면 국내 번호로 본다.
   */
  countryCode?: string | null;
};

/**
 * 국가 코드에 따라 검사 기준과 오류 메시지가 달라진다.
 * 국가 코드를 함께 입력받는 화면은 그 값을 `countryCode` 로 넘기고,
 * 국가 코드 입력에는 `deps` 로 이 항목을 걸어 함께 다시 검사되게 한다.
 */
export const phoneRules = ({
  countryCode,
  ...options
}: PhoneRuleOptions = {}) =>
  createRules<string | undefined>({
    defaultLabel: '전화번호',
    isValid: (value) => isValidPhoneOf(countryCode, value),
    message: () =>
      isKoreaCountryCode(countryCode)
        ? PHONE_MESSAGE
        : INTERNATIONAL_PHONE_MESSAGE,
  })(options);

/**
 * 국가 구분 없는 전화번호: 숫자만 4~15자리. 앞자리 0도 허용한다.
 *
 * 국가 코드를 함께 입력받지 않아 어느 나라 번호인지 알 수 없는 화면에서 쓴다.
 * 대표적으로 교사가 보는 학생 상세의 보호자 연락처 - 학생 본인 화면에서 국가 코드까지
 * 고친 값이 그대로 내려오므로, 여기서 국내 형식(0으로 시작)을 강제하면 정상 해외
 * 번호가 오류로 뜬다. 국가 코드를 함께 다루는 화면은 {@link phoneRules} 를 쓴다.
 *
 * 15자리는 국가 코드를 포함한 E.164 상한이고, 입력창이 숫자만 남기므로
 * 사실상 자릿수 범위만 본다. (명백한 오타를 걸러내는 정도)
 */
export const LOOSE_PHONE_PATTERN = /^[0-9]{4,15}$/;

export const LOOSE_PHONE_MESSAGE = '숫자 4~15자리로 입력하세요';

/** 하이픈을 제거한 뒤 국가 구분 없는 전화번호 형식을 검사한다. */
export const isValidLoosePhone = (value?: string) =>
  LOOSE_PHONE_PATTERN.test(normalizePhone(value ?? ''));

export const loosePhoneRules = createRules<string | undefined>({
  defaultLabel: '전화번호',
  isValid: isValidLoosePhone,
  message: () => LOOSE_PHONE_MESSAGE,
});

//===============================================================================================================
// 생년월일

export const BIRTHDAY_MESSAGE = '연도, 월, 일을 모두 입력해주세요';

/** 완전한 생일 값(YYYY-MM-DD)인지 확인한다. */
export const isValidBirthday = (value?: string | Date) => {
  if (!value) return false;
  if (value instanceof Date) return true;
  return /^\d{4}-\d{2}-\d{2}/.test(value);
};

/**
 * `BirthdayInput` 은 연/월만 고른 미완성 값('1990-01-')도 그대로 올려보내므로 여기서 막는다.
 */
export const birthdayRules = createRules<string | Date | undefined>({
  defaultLabel: '생년월일',
  isValid: isValidBirthday,
  message: () => BIRTHDAY_MESSAGE,
});

//===============================================================================================================
// 이름

/** 이름 길이 제한. 앞뒤 공백을 제외한 글자 수 기준. */
export const NAME_MAX_LENGTH = 20;

export const NAME_MIN_LENGTH = 2;

export const NAME_PLACEHOLDER = `${NAME_MIN_LENGTH}자 이상 ${NAME_MAX_LENGTH}자 이내의 한글, 영문 입력`;

/**
 * 완성되지 않은 한글 낱자(ㄱ, ㅏ, ㅘ 등).
 * 호환 자모(U+3131~U+318E)와 한글 자모(U+1100~U+11FF), 확장 자모 영역을 모두 본다.
 */
const HANGUL_JAMO_PATTERN = /[ᄀ-ᇿ㄰-㆏ꥠ-꥿ힰ-퟿]/;

export const NAME_JAMO_MESSAGE = '자음이나 모음만으로는 입력할 수 없습니다';

/** 자음 / 모음 낱자가 섞여 있는지 확인한다. ('ㄱㄴ', '김ㅁ' 모두 해당) */
const hasHangulJamo = (value: string) => HANGUL_JAMO_PATTERN.test(value);

/**
 * 이름에 쓸 수 있는 문자: 완성형 한글, 영문, 그리고 단어 사이 공백.
 * '남궁 민수' 처럼 중간에 공백이 들어가는 이름까지 받되, 숫자와 특수문자는 막는다.
 */
const NAME_CHARACTER_PATTERN = /^[가-힣a-zA-Z ]+$/;

export const NAME_CHARACTER_MESSAGE = '한글, 영문만 입력해주세요';

/** 허용 문자로만 이루어져 있는지 확인한다. */
const hasOnlyNameCharacters = (value: string) =>
  NAME_CHARACTER_PATTERN.test(value);

/**
 * 이름 형식 검사: 앞뒤 공백을 제외하고 {@link NAME_MIN_LENGTH} 자 이상
 * {@link NAME_MAX_LENGTH} 자 이하의 한글 / 영문 조합.
 * 공백만 입력한 값과 길이 미달 · 초과, 완성되지 않은 한글 낱자,
 * 그리고 숫자 · 특수문자를 걸러낸다.
 */
export const isValidName = (value?: string) => {
  const name = (value ?? '').trim();
  return (
    name.length >= NAME_MIN_LENGTH &&
    name.length <= NAME_MAX_LENGTH &&
    !hasHangulJamo(name) &&
    hasOnlyNameCharacters(name)
  );
};

/** 공백만 입력한 값은 `required` 를 통과해버리므로 형식 검사에서 함께 막는다. */
export const nameRules = createRules<string | undefined>({
  defaultLabel: '이름',
  isValid: isValidName,
  message: (label, value) => {
    const name = (value ?? '').trim();
    if (!name) return `${objectParticle(label)} 입력해주세요`;
    if (hasHangulJamo(name))
      return `${topicParticle(label)} ${NAME_JAMO_MESSAGE}`;
    if (!hasOnlyNameCharacters(name))
      return `${topicParticle(label)} ${NAME_CHARACTER_MESSAGE}`;
    if (name.length < NAME_MIN_LENGTH)
      return `${topicParticle(label)} ${NAME_MIN_LENGTH}자 이상으로 입력해주세요`;
    return `${topicParticle(label)} ${NAME_MAX_LENGTH}자 이내로 입력해주세요`;
  },
});

//===============================================================================================================
// 영어 이름

/**
 * 영어 이름: 영문 대문자 2~20자.
 * 단어 사이 공백은 허용하되 앞뒤 공백은 허용하지 않는다.
 */
export const ENGLISH_NAME_PATTERN = /^[A-Z][A-Z ]{0,18}[A-Z]$/;

export const ENGLISH_NAME_MESSAGE =
  '2자 이상 20자 이내의 영문 대문자, 공백만 입력해주세요';

export const isValidEnglishName = (value?: string) =>
  ENGLISH_NAME_PATTERN.test(value ?? '');

export const englishNameRules = createRules<string | undefined>({
  defaultLabel: '영어이름',
  isValid: isValidEnglishName,
  message: () => ENGLISH_NAME_MESSAGE,
});

//===============================================================================================================
// 반 이름

/** 반 이름 최대 길이. 앞뒤 공백을 제외한 글자 수 기준. */
export const CLASS_GROUP_NAME_MAX_LENGTH = 40;

export const CLASS_GROUP_NAME_MESSAGE = `${CLASS_GROUP_NAME_MAX_LENGTH}자 이내로 입력해주세요`;

export const CLASS_GROUP_NAME_PLACEHOLDER = `${CLASS_GROUP_NAME_MAX_LENGTH}자 이내로 입력해주세요 (필수)`;

/**
 * 반 이름 형식 검사: 앞뒤 공백을 제외하고 1자 이상 {@link CLASS_GROUP_NAME_MAX_LENGTH} 자 이하.
 * 문자 종류는 제한하지 않는다. '초3 A반 (월/수)' 처럼 특수문자와 공백을 섞어 부르는 이름이
 * 많아서, 공백만 입력한 값과 길이 초과만 걸러낸다.
 */
export const isValidClassGroupName = (value?: string) => {
  const name = (value ?? '').trim();
  return name.length > 0 && name.length <= CLASS_GROUP_NAME_MAX_LENGTH;
};

/** 공백만 입력한 값은 `required` 를 통과해버리므로 형식 검사에서 함께 막는다. */
export const classGroupNameRules = createRules<string | undefined>({
  defaultLabel: '반 이름',
  isValid: isValidClassGroupName,
  message: (label, value) => {
    if (!(value ?? '').trim()) return `${objectParticle(label)} 입력해주세요`;
    return `${topicParticle(label)} ${CLASS_GROUP_NAME_MESSAGE}`;
  },
});

//===============================================================================================================
// 반 설명

/** 반 설명 최대 길이. 앞뒤 공백을 제외한 글자 수 기준. */
export const CLASS_GROUP_DESCRIPTION_MAX_LENGTH = 200;

export const CLASS_GROUP_DESCRIPTION_MESSAGE = `${CLASS_GROUP_DESCRIPTION_MAX_LENGTH}자 이내로 입력해주세요`;

export const CLASS_GROUP_DESCRIPTION_PLACEHOLDER =
  '반 설명을 입력해주세요 (선택, 200자 이내)';

/**
 * 반 설명 형식 검사: 앞뒤 공백을 제외하고 {@link CLASS_GROUP_DESCRIPTION_MAX_LENGTH} 자 이하.
 * 설명은 선택 항목이라 비어 있어도 통과한다.
 */
export const isValidClassGroupDescription = (value?: string) => {
  return (value ?? '').trim().length <= CLASS_GROUP_DESCRIPTION_MAX_LENGTH;
};

export const classGroupDescriptionRules = createRules<string | undefined>({
  defaultLabel: '반 설명',
  isValid: isValidClassGroupDescription,
  message: (label) =>
    `${topicParticle(label)} ${CLASS_GROUP_DESCRIPTION_MESSAGE}`,
});
